"use client";

import { useMemo, useState, useTransition } from "react";
import Link from "next/link";
import { ALL, CLASS_ORDER, LANG_ORDER, cascadingOptions } from "../_filter-utils";
import { adjustUnitateCountValue } from "./actions";
import { IsbnField } from "@/app/_components/IsbnField";
import { formatIsbn } from "@/lib/domain/isbn";

export interface UnitateRow {
  unitateId: string;
  count: number;
  anScolar: string;
  clasa: string;
  disciplina: string;
  titluManual: string;
  autori: string;
  editura: string;
  nrActAprobare: string;
  limba: string;
  isbn: string;
  needsReview: boolean;
}

export function UnitateCatalogBrowser({ rows: initialRows, canEdit }: { rows: UnitateRow[]; canEdit: boolean }) {
  const [rows, setRows] = useState(initialRows);
  const [anScolar, setAnScolar] = useState(ALL);
  const [clasa, setClasa] = useState(ALL);
  const [disciplina, setDisciplina] = useState(ALL);
  const [limba, setLimba] = useState(ALL);
  const [editura, setEditura] = useState(ALL);
  const [onlyReview, setOnlyReview] = useState(false);
  const [isbnQuery, setIsbnQuery] = useState("");

  const active = useMemo(
    () => ({ anScolar, clasa, disciplina, limba, editura }),
    [anScolar, clasa, disciplina, limba, editura],
  );
  const fields = useMemo(
    () =>
      rows.map((r) => ({
        anScolar: r.anScolar,
        clasa: r.clasa,
        disciplina: r.disciplina,
        limba: r.limba,
        editura: r.editura,
      })),
    [rows],
  );

  const anScolarOptions = useMemo(() => cascadingOptions(fields, active, "anScolar").reverse(), [fields, active]);
  const clasaOptions = useMemo(() => cascadingOptions(fields, active, "clasa", CLASS_ORDER), [fields, active]);
  const disciplinaOptions = useMemo(() => cascadingOptions(fields, active, "disciplina"), [fields, active]);
  const limbaOptions = useMemo(() => cascadingOptions(fields, active, "limba", LANG_ORDER), [fields, active]);
  const edituraOptions = useMemo(() => cascadingOptions(fields, active, "editura"), [fields, active]);

  const reviewCount = useMemo(() => rows.filter((r) => r.needsReview).length, [rows]);

  const filtered = useMemo(() => {
    return rows.filter(
      (r) =>
        (anScolar === ALL || r.anScolar === anScolar) &&
        (clasa === ALL || r.clasa === clasa) &&
        (disciplina === ALL || r.disciplina === disciplina) &&
        (limba === ALL || r.limba === limba) &&
        (editura === ALL || r.editura === editura) &&
        (!onlyReview || r.needsReview) &&
        (isbnQuery === "" || r.isbn.includes(isbnQuery)),
    );
  }, [rows, anScolar, clasa, disciplina, limba, editura, onlyReview, isbnQuery]);

  const total = filtered.reduce((sum, r) => sum + r.count, 0);

  function handleAdjusted(unitateId: string, count: number) {
    setRows((prev) => prev.map((r) => (r.unitateId === unitateId ? { ...r, count } : r)));
  }

  return (
    <div>
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <FilterSelect label="An școlar" value={anScolar} onChange={setAnScolar} options={anScolarOptions} />
        <FilterSelect label="Clasă" value={clasa} onChange={setClasa} options={clasaOptions} />
        <FilterSelect label="Disciplină" value={disciplina} onChange={setDisciplina} options={disciplinaOptions} />
        <FilterSelect label="Limbă" value={limba} onChange={setLimba} options={limbaOptions} />
        <FilterSelect label="Editură" value={editura} onChange={setEditura} options={edituraOptions} />
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Caută după ISBN</label>
          <IsbnField value={isbnQuery} onChange={setIsbnQuery} placeholder="978-606-…" />
        </div>
      </div>

      {reviewCount > 0 ? (
        <label className="mb-4 flex w-fit items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <input type="checkbox" checked={onlyReview} onChange={(e) => setOnlyReview(e.target.checked)} />
          Arată doar de verificat ({reviewCount})
        </label>
      ) : null}

      <p className="mb-3 text-sm text-slate-500">
        {filtered.length} manuale · {total} exemplare total
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((r) => (
          <div
            key={r.unitateId}
            className={`rounded-xl border bg-white p-4 shadow-sm ${
              r.needsReview ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"
            }`}
          >
            <Link href={`/evidenta/catalog-unitate/${r.unitateId}`} className="block hover:opacity-80">
              {r.needsReview ? (
                <span className="mb-2 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  ⚠ De verificat
                </span>
              ) : null}
              <p className="font-medium text-slate-900">{r.titluManual}</p>
              <p className="mt-1 text-sm text-slate-500">{r.autori || "—"}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                  Clasa {r.clasa}
                </span>
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                  {r.disciplina}
                </span>
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                  {r.limba}
                </span>
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                  {r.anScolar}
                </span>
              </div>
              <div className="mt-3 space-y-0.5 text-xs text-slate-500">
                <p>{r.editura}</p>
                {r.nrActAprobare ? <p>Act aprobare: {r.nrActAprobare}</p> : null}
                {r.isbn ? <p>ISBN: {formatIsbn(r.isbn)}</p> : null}
              </div>
            </Link>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-900">Total: {r.count}</span>
              {canEdit ? (
                <QuickStepper unitateId={r.unitateId} count={r.count} onChanged={(c) => handleAdjusted(r.unitateId, c)} />
              ) : null}
            </div>
          </div>
        ))}
        {filtered.length === 0 ? (
          <p className="col-span-full py-8 text-center text-sm text-slate-400">
            Niciun manual în Catalog Manuale Unitate Școlară{rows.length > 0 ? " pentru filtrele alese" : ""}.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function QuickStepper({
  unitateId,
  count,
  onChanged,
}: {
  unitateId: string;
  count: number;
  onChanged: (count: number) => void;
}) {
  const [pending, startTransition] = useTransition();

  function adjust(delta: number) {
    if (delta < 0 && count <= 0) return;
    startTransition(async () => {
      const { count: next } = await adjustUnitateCountValue(unitateId, delta);
      onChanged(next);
    });
  }

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        disabled={pending || count <= 0}
        onClick={(e) => {
          e.preventDefault();
          adjust(-1);
        }}
        className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        aria-label="Scade cu unu"
      >
        −
      </button>
      <button
        type="button"
        disabled={pending}
        onClick={(e) => {
          e.preventDefault();
          adjust(1);
        }}
        className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        aria-label="Crește cu unu"
      >
        +
      </button>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
      >
        <option value={ALL}>Toate</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
